import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, BookOpen } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal } from "@/components/Reveal";
import usePageMeta from "@/hooks/usePageMeta";

const CREDENTIALS = [
  ["BSIT", "Bachelor's degree in Information Technology"],
  ["LC", "Licensed Cosmetologist — New York & Arizona"],
  ["CPCP", "Certified Permanent Cosmetics Professional"],
  ["CMLT", "Licensed Cosmetic Laser Technician — AZ"],
  ["LSO", "Laser Safety Officer"],
  ["NYSED", "Licensed New York State Educator · SPCP Trainer Member"],
];

function VideoBlock() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <div className="max-w-md mx-auto" data-testid="about-video-block">
      <div className="relative bg-espresso overflow-hidden rounded-3xl">
        <video
          ref={videoRef}
          src="/media/sherry-intro.mp4"
          preload="metadata"
          playsInline
          data-testid="about-video-player"
          className="w-full aspect-[4/5] object-cover"
          onEnded={() => setPlaying(false)}
        />
        <button
          onClick={toggle}
          data-testid="video-play-button"
          aria-label={playing ? "Pause video" : "Play video"}
          className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
            playing ? "opacity-0 hover:opacity-100 bg-transparent" : "bg-espresso/40"
          }`}
        >
          <span className="w-16 h-16 rounded-full border border-champagne bg-espresso/70 flex items-center justify-center text-champagne">
            {playing ? <Pause size={22} /> : <Play size={22} className="ml-1" />}
          </span>
        </button>
      </div>
      <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-taupe text-center">
        Watch Sherry explain what she wishes she knew — {playing ? "playing" : "tap play"}
      </p>
    </div>
  );
}

export default function About() {
  usePageMeta(
    "About Sherry Corvino | Beauté Solutions — Peoria, AZ",
    "Meet Sherry Corvino — 25+ years in beauty, 14,000+ procedures, NY State Educator, patent inventor, and her personal weight-loss journey that became the memoir 'Cosmetic Body Beyond the Border'."
  );

  return (
    <div className="grain bg-cream">
      <PageHero
        eyebrow="About Beauté Solutions"
        lines={["The honest expert", "behind the needle."]}
        lead="Sherry Corvino — CEO and founder of The SHALE Group — has spent more than 25 years in the beauty industry, completing over 14,000 cosmetic procedures from Buffalo, New York to Peoria, Arizona."
        image="/images/sherry-portrait.jpg"
        imageAlt="Sherry Corvino, CEO of Beauté Solutions"
      />

      <section className="pb-20 lg:pb-28" data-testid="about-video-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal className="text-center mb-12">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-bronze mb-4">In her own words</p>
            <h2 className="font-serif text-3xl sm:text-5xl text-ink leading-tight">
              A personal introduction
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <VideoBlock />
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-espresso text-cream" data-testid="about-story-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="img-frame">
                <img
                  src="/images/memoir-cover.webp"
                  alt="Cosmetic Body Beyond the Border — memoir by Sherry Corvino"
                  data-testid="story-portrait"
                  className="w-full object-cover aspect-[3/4] max-h-[540px]"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-champagne mb-6">Her journey</p>
              <h2 className="font-serif text-3xl sm:text-5xl leading-tight">A better version of me — over 50.</h2>
              <div className="mt-8 space-y-6 text-cream/70 leading-relaxed max-w-2xl">
                <p>
                  Sherry began her company the very same month she underwent bariatric
                  surgery — experiencing her own life-changing weight loss. She's open
                  about that journey, and about what it taught her: "Life is rough, and
                  every decade brings more complications. Through our esthetics and
                  wellness services, we help clients internalize their beauty — which is
                  a very empowering thing. When you feel amazing, you can get through anything."
                </p>
                <p>
                  That firsthand honesty became a book. In "Cosmetic Body Beyond the
                  Border," Sherry shares everything she learned traveling beyond the
                  border for her own procedures — the research, the surgeon comparisons,
                  the planning and the recovery — written so other women can make an
                  informed decision instead of a rushed one.
                </p>
              </div>
              <a
                href="https://mycosmeticsbeyond.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="memoir-link"
                className="group mt-10 inline-flex items-center gap-2.5 rounded-full bg-gold text-espresso px-8 py-4 text-sm font-semibold transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-golddark hover:text-white"
              >
                <BookOpen size={15} />
                The memoir & guide
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="about-credentials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal className="mb-14">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-bronze mb-4">Credentials</p>
            <h2 className="font-serif text-3xl sm:text-5xl text-ink leading-tight max-w-2xl">
              A long string of letters after her name — and she means it.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line">
            {CREDENTIALS.map(([abbr, desc], i) => (
              <Reveal key={abbr} delay={(i % 3) * 0.08}>
                <div className="bg-cream h-full p-8" data-testid="credential-card">
                  <p className="font-serif italic text-3xl text-wine">{abbr}</p>
                  <p className="mt-4 text-sm text-taupe leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-10 text-taupe leading-relaxed max-w-3xl">
              With expertise spanning laser tattoo removal, facial resurfacing and
              permanent makeup, Sherry is an all-around expert providing the services —
              and the beauty recommendations — that create life-changing makeovers.
              Her work has been featured in numerous national publications, as well as
              on TV and radio.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-espresso text-cream" data-testid="commitment-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <p className="font-serif italic text-xl sm:text-2xl text-gold mb-8">My commitment</p>
            <blockquote className="font-serif text-2xl sm:text-4xl leading-snug text-cream/90">
              "At the heart of my business is a lifelong commitment to my customers —
              because my clients are for life. You'll always get straightforward answers
              with no hype — just the expert advice you need to make the best decisions."
            </blockquote>
            <p className="mt-8 font-semibold text-gold text-sm">Sherry Corvino — BSIT, LC, CPCP, CMLT, LSO</p>
            <p className="mt-8 text-cream/60 leading-relaxed max-w-2xl mx-auto">
              An industry innovator, New York State Educator and design-patent inventor,
              Sherry built Beauté Solutions to be an all-in-one destination for permanent
              cosmetics, laser esthetics and skin health — from scar minimization and
              areola restoration to anti-aging laser work.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-cream2 border-y border-line" data-testid="press-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="font-serif italic text-xl sm:text-2xl text-gold mb-6">In the press</p>
            <h2 className="font-serif text-3xl sm:text-5xl text-ink leading-tight max-w-3xl">
              When permanent eyeliner goes wrong, the industry calls Sherry.
            </h2>
            <p className="mt-6 text-taupe leading-relaxed max-w-3xl">
              Featured by EIN Newswires and Buffalo Healthcare Profiles — and named a
              trail-blazing medical spa in the Phoenix valley — Sherry is one of the few
              experts in the world who can remove and correct botched permanent makeup.
              About 90% of her laser eyeliner-removal clients fly in from out of state,
            </p>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-3 gap-px bg-line border border-line">
            {[
              ["EIN Newswires", '"When Permanent Eyeliner Goes Wrong: Expert Sherry Corvino Offers Solutions, Changes Lives" — 2021'],
              ["Buffalo Healthcare Profiles", '"Embracing the Power of Healing from the Outside In" — a growing brand providing a unique mix of Medi spa services'],
              ["Phoenix Valley", "Named a trail-blazing Medi spa where Sherry herself provides the expert services behind life-changing makeovers"],
            ].map(([source, quote], i) => (
              <Reveal key={source} delay={i * 0.08}>
                <div className="bg-cream h-full p-8" data-testid="press-card">
                  <p className="font-serif italic text-xl text-wine">{source}</p>
                  <p className="mt-4 text-sm text-taupe leading-relaxed">{quote}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection title="Meet Sherry — start with a conversation" />
    </div>
  );
}
